import request from "src/utils/request";
import { loadingAction } from "src/redux/actions/userActions";
import { toastify } from "src/utils/toast";
import {
  buildVipAccountPayload,
  parseVipStatsResponse,
  resolveVipUsername,
} from "./utils";

const parseVipsFromStatsResponse = (response, orders = []) => {
  const data = response?.data?.data;
  const candidates =
    data?.vips ||
    data?.vip_accounts ||
    data?.users ||
    data?.accounts ||
    data?.vips_accounts ||
    data;

  if (Array.isArray(candidates) && candidates.length > 0) {
    return candidates.map((vip) => ({
      id: vip?.id ?? vip?.vip_id,
      name:
        vip?.name ||
        vip?.vip_name ||
        vip?.full_name ||
        `VIP #${vip?.id ?? vip?.vip_id}`,
      username: resolveVipUsername(vip),
      email: vip?.email || vip?.vip_email || "",
    }));
  }

  const map = new Map();
  orders.forEach((order) => {
    const vipId = order?.vips_approved_by;
    if (vipId == null || vipId === "") return;

    const existing = map.get(String(vipId));
    if (!existing) {
      map.set(String(vipId), {
        id: vipId,
        name:
          order?.vips_approved_by_name ||
          order?.vips_name ||
          order?.approved_by_name ||
          `VIP #${vipId}`,
        username: "",
        email: "",
      });
    }
  });

  return Array.from(map.values());
};

const buildVipFilterPayload = ({
  vipsId,
  remakeFeedback,
  startDate,
  endDate,
} = {}) => {
  const payload = {};

  if (vipsId != null && vipsId !== "") {
    payload.vips_id = vipsId;
  }
  if (remakeFeedback) {
    payload.remake_feedback = remakeFeedback;
  }
  if (startDate) {
    payload.start_date = startDate;
  }
  if (endDate) {
    payload.end_date = endDate;
  }

  return payload;
};

const fetchVipEndpointData = async (
  dispatch,
  endpoint,
  filters = {},
  { useGlobalLoading = true, errorMessage } = {},
) => {
  if (useGlobalLoading) {
    dispatch(loadingAction(true));
  }

  try {
    const payload = buildVipFilterPayload(filters);
    const res = await request.post(endpoint, payload);
    const parsed = parseVipStatsResponse(res);
    const orders = parsed?.orders || [];
    const vipsAccounts =
      parsed?.vipSummaries?.length > 0
        ? parsed.vipSummaries.map((vip) => ({
            id: vip.id,
            name: vip.name,
            username: vip.username || "",
            email: vip.email || "",
          }))
        : parseVipsFromStatsResponse(res, orders);
    if (useGlobalLoading) {
      dispatch(loadingAction(false));
    }

    return {
      orders,
      vipsAccounts,
      vipSummaries: parsed?.vipSummaries || null,
      response: res,
    };
  } catch (e) {
    if (useGlobalLoading) {
      dispatch(loadingAction(false));
    }
    toastify(
      "error",
      e.response?.data?.message || errorMessage,
    );
    return { orders: [], vipsAccounts: [], vipSummaries: null, response: null };
  }
};

export const fetchVipStatsData = async (
  dispatch,
  filters = {},
  options = {},
) =>
  fetchVipEndpointData(dispatch, "/orders/stats/vips", filters, {
    ...options,
    errorMessage: "Failed to fetch VIP stats",
  });

export const fetchVipPerformanceData = async (
  dispatch,
  filters = {},
  options = {},
) =>
  fetchVipEndpointData(dispatch, "/orders/performance/vips", filters, {
    ...options,
    errorMessage: "Failed to fetch VIP performance",
  });

export const saveVip = async (fields) => {
  const res = await request.post("/users/vips", buildVipAccountPayload(fields));
  return res?.data;
};

export const createVip = saveVip;

export const loginVip = async ({ username, pin }) => {
  const res = await request.post("/login", {
    email: username,
    password: pin,
  });
  return res?.data;
};
